import { getApiUrl } from "./getApiUrl";

const ENDPOINT_PATH = "/psp/trustpayments/widget-debug-log";
const LEVELS = ["log", "info", "warn", "error", "debug"] as const;

type Level = (typeof LEVELS)[number];

const safeStringify = (value: unknown): unknown => {
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  if (value && typeof value === "object") {
    try {
      JSON.stringify(value);
      return value;
    } catch {
      return String(value);
    }
  }
  return value;
};

// Re-entrancy guard: `getApiUrl()` calls `getSafeApiUrl()` which logs
// a `console.warn` when nothing resolves. The console wrappers below
// route every warn through `send` again — without this flag a single
// unresolved URL would recurse until the JS stack blows
// ("Maximum call stack size exceeded").
let isSending = false;

const send = (level: Level, args: unknown[]) => {
  if (isSending) return;
  isSending = true;
  try {
    const apiUrl = getApiUrl();
    if (!apiUrl) return;
    const url = `${apiUrl.replace("/graphql", "")}${ENDPOINT_PATH}`;
    const body = JSON.stringify({
      level,
      ts: new Date().toISOString(),
      href: window.location.href,
      args: args.map(safeStringify),
    });
    try {
      if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        const blob = new Blob([body], { type: "application/json" });
        if (navigator.sendBeacon(url, blob)) return;
      }
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
        body,
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      // never throw from a logger
    }
  } finally {
    isSending = false;
  }
};

let installed = false;

export const installRemoteLogger = (): void => {
  if (installed) return;
  installed = true;

  for (const level of LEVELS) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      original(...args);
      send(level, args);
    };
  }

  window.addEventListener("error", (event) => {
    send("error", [
      "window.onerror",
      event.message,
      `${event.filename}:${event.lineno}:${event.colno}`,
      event.error?.stack ?? null,
    ]);
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    send("error", ["unhandledrejection", reason instanceof Error ? { message: reason.message, stack: reason.stack } : reason]);
  });
};
